// src/contexts/UserContext.types.ts

// SHARED TYPES

export type GreetingStyle = "formal" | "casual" | "none";

export type UserRole =
  | "doctor"
  | "partner"
  | "insurance"
  | "laboratory";

export type ConsultationMode =
  | "in-person"
  | "google-meet"
  | "phone-call";

export type ConsultationType =
  | "new-consultation"
  | "follow-up"
  | "emergency"
  | "urgent-review";

export type ConsultationStatus =
  | "pending"
  | "confirmed"
  | "completed"
  | "canceled"
  | "active";

export type TestPriority =
  | "routine"
  | "urgent"
  | "stat";

export type InsuranceStatus =
  | "pending_verification"
  | "verified"
  | "rejected"
  | "failed"
  | "expired"
  | "uninsured";

// PROFILE INTERFACES

/**
 * Emergency facility information for partner profiles.
 */
export interface EmergencyFacility {
  id: string;
  hospitalName: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  emergencyContact: string;
  ambulanceNumber: string;
  ambulanceAvailability: boolean;
  responseTime: string;
  services: string[];
  facilities: string[];
  contactPerson: string;
  contactTitle: string;
  notes: string;
}

/**
 * Doctor profile information.
 */
export interface DoctorProfile {
  id: string;
  fullName: string;
  title: string;
  bio: string;
  email: string;
  phone: string;
  clinicName: string;
  specializations: string[];
  yearsOfExperience: number;
  licenseNumber?: string;
  avatar?: string | null;
  greetingStyle?: GreetingStyle;
}

/**
 * Partner profile for healthcare facilities.
 *
 * Represents hospitals, clinics and other healthcare partners.
 */
export interface PartnerProfile {
  id: string;
  facilityName: string;
  facilityType: string;

  partnershipType:
    | "hospital"
    | "clinic"
    | "insurance"
    | "";

  insuranceHmoType: string;
  insuranceHmoName: string;
  policyTypes: string[];
  networkSize?: number;
  regulatoryBody: string;
  licenseNumber: string;
  bio: string;
  email: string;
  phone: string;
  address: string;
  website?: string;
  numberOfBeds?: number;
  departments: string[];
  services: string[];
  accreditations: string[];
  operatingHours: string;
  emergencyServices: boolean;
  administratorName: string;
  administratorTitle: string;
  registrationNumber: string;
  avatar?: string | null;
  emergencyFacilities?: EmergencyFacility[];
  greetingStyle?: GreetingStyle;
  accreditation?: string;
}

/**
 * Insurance provider profile.
 */
export interface InsuranceProfile {
  id: string;
  companyName: string;
  insuranceType: string;
  registrationNumber?: string;
  licenseNumber: string;
  email: string;
  phone: string;
  address: string;
  website?: string;
  policyTypes?: string[];
  networkSize?: number;
  regulatoryBody?: string;
  administratorName?: string;
  administratorTitle?: string;
  avatar?: string | null;
  greetingStyle?: GreetingStyle;
  coverageAreas?: string;
  establishedDate?: string;
}

/**
 * Laboratory or diagnostic facility profile.
 */
export interface LaboratoryProfile {
  id: string;
  labName: string;

  labType:
    | "clinical"
    | "diagnostic"
    | "pathology"
    | "reference";

  registrationNumber: string;
  licenseNumber: string;
  email: string;
  phone: string;
  address: string;
  website?: string;
  availableTests: string[];
  certifications: string[];
  equipmentCapacity: number;
  turnaroundTime: string;
  operatingHours: string;
  directorName: string;
  directorTitle: string;
  avatar?: string | null;
  greetingStyle?: GreetingStyle;
  city?: string;
  state?: string;
  country?: string;
  primaryEmail?: string;
  primaryPhone?: string;
  licenseCertificate?: string;
  certificationDocs?: string;
  accreditation?: string;
}

/**
 * Union of every supported profile type.
 */
export type UserProfile =
  | DoctorProfile
  | PartnerProfile
  | InsuranceProfile
  | LaboratoryProfile;

// USER AND AUTHENTICATION TYPES

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  specialization?: string;
  avatar?: string | null;
  hasCompletedOnboarding?: boolean;
  profile?: UserProfile;
}

// PATIENT AND HEALTH DATA TYPES

export interface HealthcareProvider {
  name: string;
  type: string;
  phone: string;
  nextAppt?: string;
}

export interface Patient {
  id: string;
  name: string;

  /**
   * Both fields are retained to support existing data.
   * Prefer dateOfBirth for new code.
   */
  dob?: string;
  dateOfBirth?: string;

  email?: string;
  phone?: string;
  avatar?: string;
  picture?: string;
  age?: number;
  gender?: string;
  diabetesType?: string;
  contact?: string;
  healthHistory?: string;
  address?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  currentMedications?: string;
  allergies?: string;
  bloodType?: string;
  height?: string;
  weight?: string;
  smokingStatus?: string;
  alcoholConsumption?: string;
  exerciseFrequency?: string;
  chronicConditions?: string;
  familyHistory?: string;

  // Insurance
  hasInsurance: boolean;
  insuranceInfo?: string;
  insuranceCoveragePercentage?: number;
  insuranceProviderId?: string;
  insurancePolicyNumber?: string;
  insuranceStatus?: InsuranceStatus;

  // Clinical information
  comorbidities?: string[] | string;
  complications?: string;
  hospitalizations?: string;
  surgeries?: string;
  hba1c?: number;
  hba1cTarget?: string;
  glucoseTarget?: string;
  timeInRange?: number;
  insulinToCarbRatio?: string;
  correctionFactor?: string;
  basalRate?: string;

  healthcareProviders?: HealthcareProvider[];

  // Lifestyle information
  sleepHours?: number;
  sleepQuality?: number;
  stressLevel?: number;
  dietaryRestrictions?: string;

  // Hypoglycaemia and emergency information
  hypoSymptoms?: string;
  hypoTreatment?: string;
  glucagonLocation?: string;
  emergencyInstructions?: string;
  hospitalPreference?: string;
}

// PRESCRIPTION AND TEST ORDER TYPES

export interface Prescription {
  type: "prescription";
  medication: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions: string;
  insuranceCovered: boolean;
  coveragePercentage: number;
  quantity?: string;
  refills?: string;
  route?: string;
  destination?: "patient" | "pharmacy";
  pharmacy?: string;
}

/**
 * Laboratory test order information.
 */
export interface TestOrder {
  type: "test";
  testName: string;
  instructions: string;
  notes?: string;
  insuranceCovered: boolean;
  coveragePercentage: number;
  reason?: string;
  priority?: TestPriority;
  destination?: "patient" | "lab";
  laboratory?: string;
}

/**
 * Union of clinical items that may be attached to a consultation.
 */
export type DetailedPrescription =
  | Prescription
  | TestOrder;

// APPOINTMENT AND CONSULTATION TYPES

/**
 * Fields shared between appointments and consultations.
 */
export interface ConsultationBase {
  id: string;
  patientId: string;
  patientName?: string;
  doctorId: string;
  date: string;
  time: string;
  mode: ConsultationMode;
  status: ConsultationStatus;
  type?: ConsultationType;
  reason?: string;
  notes?: string;
  outcomeNotes?: string;
  healthInfo?: string;

  /**
   * Google Meet URL for Google Meet consultations.
   */
  meetingLink?: string;

  /**
   * Physical address for in-person consultations.
   */
  location?: string;

  /**
   * Contact number for phone-call consultations.
   */
  contactPhone?: string;
}

/**
 * Scheduled patient appointment.
 */
export interface Appointment
  extends ConsultationBase {
  patientName: string;
  type: ConsultationType;
  prescriptions?: string[];
  detailedPrescriptions?: DetailedPrescription[];
}

/**
 * Consultation record.
 */
export interface Consultation
  extends ConsultationBase {
  prescriptions?: DetailedPrescription[];
}

/**
 * Calendar-sync payload accepted by the user context.
 *
 * This avoids an unsafe cast when a consultation is sent to the
 * Google Calendar synchronization function.
 */
export type CalendarSyncItem =
  | Appointment
  | Consultation;

// CONTEXT TYPE

export interface UserContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  hasCompletedOnboarding: boolean;
  loading: boolean;

  isLoadingAppointments: boolean;
  isLoadingPatients: boolean;

  patients: Patient[];
  appointments: Appointment[];
  consultations: Consultation[];

  login: (
    userData: User,
    token: string
  ) => Promise<void>;

  logout: () => Promise<void>;

  updateProfile: (
    updates: Partial<UserProfile>
  ) => Promise<void>;

  updateUser: (
    updates: Partial<User>
  ) => void;

  completeOnboarding: (
    profileData?: UserProfile,
    role?: User["role"]
  ) => Promise<void>;

  refreshData: () => Promise<void>;

  fetchPatients: () => Promise<void>;

  fetchAppointments: () => Promise<void>;

  fetchConsultations: () => Promise<void>;

  addPatient: (
    patient: Patient
  ) => Promise<void>;

  updatePatient: (
    id: string,
    updates: Partial<Patient>
  ) => Promise<void>;

  deletePatient: (
    id: string
  ) => Promise<void>;

  addAppointment: (
    appointment: Appointment
  ) => Promise<void>;

  updateAppointment: (
    id: string,
    updates: Partial<Appointment>
  ) => Promise<void>;

  deleteAppointment: (
    id: string
  ) => Promise<void>;

  updateAppointmentStatus: (
    id: string,
    status: Appointment["status"]
  ) => Promise<void>;

  addConsultation: (
    consultation: Consultation
  ) => Promise<void>;

  updateConsultation: (
    id: string,
    updates: Partial<Consultation>
  ) => Promise<void>;

  deleteConsultation: (
    id: string
  ) => Promise<void>;

  sharePrescription: (
    item: DetailedPrescription & {
      consultationId: string;
      patientId: string;
    }
  ) => Promise<void>;

  shareTestOrder: (
    testOrder: {
      consultationId: string;
      patientId: string;
      testName: string;
      instructions: string;
      notes?: string;
      reason?: string;
      priority?: TestPriority;
      destination?: "patient" | "lab";
      laboratory?: string;
    }
  ) => Promise<void>;

  
   //Accepts either a scheduled appointment or consultation.
   
  syncWithGoogleCalendar: (
    item: CalendarSyncItem
  ) => Promise<void>;

  getLabName: () => string;

  getLabContactInfo: () => {
    email: string;
    phone: string;
    address: string;
  };

  isLabUser: () => boolean;

  clearAllData: () => void;

  resetToDefaults: () => void;

  exportUserData: () => void;
}

// TYPE GUARDS

export const isDoctorProfile = (
  profile: UserProfile
): profile is DoctorProfile =>
  "fullName" in profile &&
  "clinicName" in profile;

export const isPartnerProfile = (
  profile: UserProfile
): profile is PartnerProfile =>
  "facilityName" in profile &&
  "departments" in profile;

export const isInsuranceProfile = (
  profile: UserProfile
): profile is InsuranceProfile =>
  "companyName" in profile &&
  "insuranceType" in profile;

export const isLaboratoryProfile = (
  profile: UserProfile
): profile is LaboratoryProfile =>
  "labName" in profile &&
  "availableTests" in profile;